// ============================================================
// EA USE-CASE HUB  (/ea/use-case/:id/:tab  where tab ∈ brief | rationale | roi)
//
// Storefront Full-Content Drilldown pattern: one persistent shell (identity ·
// verdict · KPIs · tabs). Tab bodies remount; chrome does not.
// Scroll resets only when the use-case id changes — not on tab switch.
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';

import { EAJourneyBar } from '../components/EAJourneyBar';
import { EAUseCaseHubShell, type HubView } from '../components/hub/EAUseCaseHubShell';
import type { EffortTag } from '../components/EffortTagPill';
import { HubChromeProvider, useHubChrome } from '../contexts/HubChromeContext';
import { EA_CLIENT_ID } from '../constants/eaClient';
import { useEA } from '../contexts/EAContext';
import { useDemoPlan } from '../../hooks/useDemoPlan';
import {
  ensureCatalogBriefSnapshot,
  fetchEvaluation,
  peekCatalogBriefSnapshot,
  peekEvaluation,
  selectUseCase,
  UCE_CATALOG_FILTERS,
  fetchOpportunities,
} from '../services/useCaseExplorerService';
import {
  buildDrilldownNav,
  readDrilldownNav,
  resolveDrilldownExit,
} from '../utils/eaDrilldownNav';
import { resolveScoreFreshness } from '../utils/formatLastComputed';
import {
  parseRecommendation,
  verdictFromScore,
  type VerdictKey,
} from '../theme/decisionTokens';

import EAUseCaseBriefPage from './EAUseCaseBriefPage';
import EAScoringRationalizationPage from './EAScoringRationalizationPageApi';
import EAROIModelPage from './EAROIModelPage';

const TOTAL_PORTFOLIO_SIZE = 175;

const SEG_TO_VIEW: Record<string, HubView> = {
  brief: 'brief',
  rationale: 'rationale',
  roi: 'roi',
  'roi-model': 'roi',
};
const VIEW_TO_SEG: Record<HubView, string> = {
  brief: 'brief',
  rationale: 'rationale',
  roi: 'roi',
};

const MD: Components = {
  p: ({ children, ...props }) => (
    <p className="text-sm text-[#717182] leading-relaxed mb-0" {...props}>
      {children}
    </p>
  ),
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-[#18365E]" {...props}>
      {children}
    </strong>
  ),
};

function fmtM2(n: number) {
  return (Number.isFinite(n) ? n : 0).toFixed(2);
}

function bootstrapDecisionText(verdict: VerdictKey): string {
  if (verdict === 'green') {
    return 'Proceed: scores and strategic fit support carrying this initiative forward.';
  }
  if (verdict === 'cond') {
    return 'Validate first: confirm key assumptions before committing implementation capacity.';
  }
  return 'Hold: revisit later unless constraints or scores improve.';
}

function HubInner() {
  const { id, tab } = useParams<{ id: string; tab: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navState = readDrilldownNav(location.state);
  const { isFreemium } = useDemoPlan();
  const { finalistIds, addFinalist } = useEA();
  const { chrome, setChrome, exportHandlerRef } = useHubChrome();

  const clientId = EA_CLIENT_ID;
  const seg = tab && SEG_TO_VIEW[tab] ? tab : 'brief';
  const view = SEG_TO_VIEW[seg] ?? 'brief';

  const [rankedIds, setRankedIds] = useState<string[]>(() =>
    Array.isArray(navState.rankedIds) ? navState.rankedIds.map(String) : [],
  );
  const [catalogTotal, setCatalogTotal] = useState(() =>
    typeof navState.catalogTotal === 'number' ? navState.catalogTotal : TOTAL_PORTFOLIO_SIZE,
  );
  const [selectionSyncError, setSelectionSyncError] = useState<string | null>(null);
  const [bootstrapping, setBootstrapping] = useState(false);

  // Provider remounts on id change (key={id}); only reset local UI state here.
  useEffect(() => {
    setSelectionSyncError(null);
  }, [id]);

  // Scroll only on use-case change — not tab switch (storefront hub pattern + DMD scroll fix).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [id]);

  // Ranked pager list when nav state is thin.
  useEffect(() => {
    if (!clientId || rankedIds.length > 0) return;
    let cancelled = false;
    void fetchOpportunities(clientId, UCE_CATALOG_FILTERS)
      .then((res) => {
        if (cancelled) return;
        const ids = (res?.data ?? []).map((o) => String(o.id));
        if (ids.length) setRankedIds(ids);
        const total =
          typeof res?.pagination?.total === 'number'
            ? res.pagination.total
            : TOTAL_PORTFOLIO_SIZE;
        setCatalogTotal(total);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [clientId, rankedIds.length]);

  // Bootstrap chrome when landing on rationale/roi before Brief has published.
  useEffect(() => {
    if (!id || !clientId) return;
    if (chrome?.useCaseId === String(id)) return;
    // Brief is the canonical publisher — let it own chrome when active.
    if (view === 'brief') return;

    let cancelled = false;
    setBootstrapping(true);

    const applyFromEval = (raw: unknown) => {
      if (cancelled || !raw || typeof raw !== 'object') return;
      const data = raw as Record<string, unknown>;
      const header = (data.header ?? data.use_case ?? data) as Record<string, unknown>;
      const overview = (data.overview ?? {}) as Record<string, unknown>;
      const title = String(
        header.title ?? header.name ?? overview.title ?? `Use Case ${id}`,
      );
      const category = String(
        header.categoryName ?? header.category_name ?? header.category ?? '',
      );
      const totalScore = Number(header.totalScore ?? header.total_score ?? header.compositeScore ?? 0);
      const rankRaw = Number(header.portfolioRank ?? header.rank ?? header.portfolio_rank);
      const rank = Number.isFinite(rankRaw) && rankRaw > 0 ? rankRaw : null;
      const recRaw = String(header.recommendation ?? header.verdict ?? '').trim();
      const parsed = parseRecommendation(recRaw);
      const verdict: VerdictKey = parsed
        ? parsed === 'PROCEED'
          ? 'green'
          : parsed === 'EVALUATE'
            ? 'cond'
            : 'hold'
        : verdictFromScore(totalScore);
      const rationale = String(
        overview.executiveDecision ?? overview.rationale ?? header.rationale ?? '',
      ).trim();
      const roiRange = String(header.roi_range ?? header.roiRange ?? '').trim();
      const annualM = Number(header.annual_benefit_m ?? header.annualBenefitM);
      const npvM = Number(header.npv_3yr_m ?? header.npv3yrM);
      const ttv = String(header.time_to_roi ?? header.timeToValue ?? header.time_to_value ?? '').trim();
      const effortRaw = String(header.implementation_effort ?? header.effort ?? 'Medium').toLowerCase();
      const effortTag: EffortTag =
        effortRaw === 'low' ? 'Quick Win' : effortRaw === 'high' ? 'High Effort' : 'Moderate';
      const lastAt = (header.last_computed_at ?? header.lastComputedAt) as string | null | undefined;
      const lastSrc = (header.last_computed_source ?? header.lastComputedSource) as
        | string
        | null
        | undefined;
      const freshness = resolveScoreFreshness(lastAt, lastSrc);
      const portfolioSize = catalogTotal || TOTAL_PORTFOLIO_SIZE;

      setChrome({
        useCaseId: String(id),
        title,
        category,
        rank,
        portfolioSize,
        effortTag,
        verdict,
        decisionContent: rationale ? (
          <ReactMarkdown components={MD}>{rationale}</ReactMarkdown>
        ) : (
          <span>{bootstrapDecisionText(verdict)}</span>
        ),
        bannerFooter: !parsed ? (
          <p className="mt-1.5 text-[10px] italic text-[#717182]">
            Verdict derived from the composite score — the evaluation payload carries no explicit
            recommendation.
          </p>
        ) : undefined,
        kpis: {
          annualBenefit: roiRange || (Number.isFinite(annualM) ? `$${fmtM2(annualM)}M` : '—'),
          npv: Number.isFinite(npvM) ? `$${fmtM2(npvM)}M` : '—',
          timeToValue: ttv || '—',
          compositeScore: Number.isFinite(totalScore) ? `${totalScore.toFixed(2)} / 100` : '—',
          portfolioRank: rank ? `#${rank} of ${portfolioSize}` : '—',
        },
        freshnessLabel:
          freshness.label || freshness.hasSource
            ? [freshness.hasSource ? (freshness.isLive ? 'Live-computed' : 'Prior computation') : null, freshness.label]
                .filter(Boolean)
                .join(' · ')
            : null,
        freshnessIsLive: freshness.isLive,
        freshnessTitle: freshness.absoluteTitle,
        recommendationDerived: !parsed,
      });
    };

    const cached = peekEvaluation(clientId, id);
    if (cached?.data) {
      applyFromEval(cached.data);
      setBootstrapping(false);
      return () => {
        cancelled = true;
      };
    }

    void fetchEvaluation(clientId, id)
      .then((payload) => {
        if (cancelled) return;
        applyFromEval(payload?.data ?? payload);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });

    return () => {
      cancelled = true;
    };
    // chrome?.useCaseId: re-check after Brief publishes so we don't keep bootstrapping
  }, [id, clientId, view, chrome?.useCaseId, catalogTotal, setChrome]);

  const exitNav = resolveDrilldownExit(navState);
  const backLabel = exitNav.label;
  const backPath = exitNav.path;
  const inPortfolio = Boolean(id) && finalistIds.has(String(id));

  const idx = rankedIds.indexOf(String(id || ''));
  const prevId = idx > 0 ? rankedIds[idx - 1] : null;
  const nextId = idx >= 0 && idx < rankedIds.length - 1 ? rankedIds[idx + 1] : null;

  const drilldownState = useMemo(
    () =>
      buildDrilldownNav(navState, {
        rankedIds,
        catalogTotal,
        briefSnapshot: chrome
          ? {
              id: String(id),
              title: chrome.title,
              category: chrome.category,
              totalScore: undefined,
            }
          : navState.briefSnapshot,
      }),
    [navState, rankedIds, catalogTotal, chrome, id],
  );

  const pageTo = useCallback(
    (targetId: string | null) => {
      if (!targetId) return;
      void (async () => {
        const catalogSnap =
          peekCatalogBriefSnapshot(clientId, targetId) ||
          (await ensureCatalogBriefSnapshot(clientId, targetId));
        navigate(`/ea/use-case/${targetId}/${VIEW_TO_SEG[view]}`, {
          state: buildDrilldownNav(navState, {
            rankedIds,
            catalogTotal,
            briefSnapshot: catalogSnap,
          }),
        });
      })();
    },
    [clientId, view, navigate, navState, rankedIds, catalogTotal],
  );

  const goTab = useCallback(
    (next: HubView) => {
      if (!id || next === view) return;
      navigate(`/ea/use-case/${id}/${VIEW_TO_SEG[next]}`, {
        state: drilldownState,
      });
    },
    [id, view, navigate, drilldownState],
  );

  const handleBack = useCallback(() => {
    if (exitNav.fromActivate) {
      navigate(backPath);
      return;
    }
    // DMD-1198: ensure Evaluate remounts on Use Case Explorer (URL + state).
    let path = backPath || '/ea/opportunities';
    try {
      const url = new URL(path, window.location.origin);
      if (!url.searchParams.get('tab')) {
        url.searchParams.set('tab', 'explore');
      }
      path = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      path = path.includes('tab=')
        ? path
        : `${path}${path.includes('?') ? '&' : '?'}tab=explore`;
    }
    navigate(path, { state: { returnToTab: 'explore' } });
  }, [navigate, backPath, exitNav.fromActivate]);

  const handleShare = useCallback(async () => {
    try {
      await navigator.clipboard?.writeText(window.location.href);
    } catch {
      /* ignore */
    }
  }, []);

  const handleProceed = useCallback(async () => {
    if (!id || !clientId) return;
    if (isFreemium) {
      navigate('/ea/upgrade');
      return;
    }
    if (inPortfolio) return;
    const ucId = parseInt(id, 10);
    if (Number.isNaN(ucId)) return;
    addFinalist(id);
    try {
      await selectUseCase(String(clientId), ucId);
      setSelectionSyncError(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Selection update failed';
      setSelectionSyncError(message);
    }
  }, [id, clientId, isFreemium, inPortfolio, addFinalist, navigate]);

  const handleOpenCommandCenter = useCallback(() => {
    if (!id) return;
    navigate(`/ea/execute/${id}`, { state: drilldownState });
  }, [id, navigate, drilldownState]);

  const handleExport = useCallback(() => {
    if (exportHandlerRef.current) {
      exportHandlerRef.current();
      return;
    }
    // Export lives on Brief — open that tab with openExport flag.
    if (!id) return;
    navigate(`/ea/use-case/${id}/brief`, {
      state: buildDrilldownNav(drilldownState, { openExport: true }),
    });
  }, [exportHandlerRef, id, navigate, drilldownState]);

  // Invalid tab → brief
  if (tab && !SEG_TO_VIEW[tab]) {
    return <Navigate to={`/ea/use-case/${id}/brief`} replace state={location.state} />;
  }

  const title = chrome?.title ?? '';
  const portfolioSize = chrome?.portfolioSize ?? catalogTotal ?? TOTAL_PORTFOLIO_SIZE;

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="px-6 pt-3 pb-1 print:hidden">
        <EAJourneyBar />
      </div>

      <EAUseCaseHubShell
        activeView={view}
        title={title || (bootstrapping ? 'Loading…' : '')}
        category={chrome?.category}
        rank={chrome?.rank ?? null}
        portfolioSize={portfolioSize}
        effortTag={chrome?.effortTag ?? null}
        inPortfolio={inPortfolio}
        verdict={chrome?.verdict}
        decisionContent={chrome?.decisionContent}
        bannerFooter={
          <>
            {chrome?.bannerFooter}
            {selectionSyncError && (
              <p className="mt-2 text-[10px] text-amber-700">
                Portfolio saved locally. Sync issue: {selectionSyncError}
              </p>
            )}
          </>
        }
        kpis={chrome?.kpis}
        freshnessLabel={chrome?.freshnessLabel}
        freshnessIsLive={chrome?.freshnessIsLive}
        freshnessTitle={chrome?.freshnessTitle}
        backLabel={backLabel}
        onBack={handleBack}
        prevId={prevId}
        nextId={nextId}
        onPage={pageTo}
        onTab={goTab}
        onShare={() => {
          void handleShare();
        }}
        onExport={handleExport}
        exportLabel="Export"
        onProceed={() => {
          void handleProceed();
        }}
        onOpenCommandCenter={handleOpenCommandCenter}
      >
        {view === 'brief' && <EAUseCaseBriefPage embedded />}
        {view === 'rationale' && <EAScoringRationalizationPage embedded />}
        {view === 'roi' && <EAROIModelPage embedded />}
      </EAUseCaseHubShell>
    </div>
  );
}

export default function EAUseCaseHubPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <HubChromeProvider key={id}>
      <HubInner />
    </HubChromeProvider>
  );
}
